/**
 * Reads the previewed object's content (TanStack Query). Moving through the grid with the arrow keys settles for
 * `SETTLE_MS` before a read starts, a superseded read is cancelled in main (`azure-storage:cancel`), and the previous
 * preview stays on screen (dimmed by the pane) until the next one arrives — no blank flash between files. Results are
 * keyed by the object's version (ETag), so an unchanged object re-opens from the cache and a changed one is re-read;
 * large reads are dropped as soon as nothing shows them.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import type { StorageOperationError } from '../../../../types/azureStorage';
import type { StorageContentReadResult } from '../../../../types/azureStorageContent';
import { cancelStorageRequest, newStorageRequestId, readStorageContent, toStorageOperationError } from '../services/azureStorageApi';
import { previewReadPlan, previewTypeOf, type StoragePreviewType } from './previewKinds';
import type { StoragePreviewObject } from './previewTarget';

const SETTLE_MS = 120;
const READ_TIMEOUT_MS = 60_000;
const CACHE_MS = 60_000;
const LARGE_READ_BYTES = 4 * 1024 * 1024;

/** One read: the bytes and facts, with the object and viewer type they belong to (a dimmed previous preview keeps both). */
export type StoragePreviewContent = StorageContentReadResult & { objectKey: string; versionKey: string; object: StoragePreviewObject; type: StoragePreviewType };

export type StoragePreviewContentState = {
  /** The object the pane shows now (after the settle delay). */
  object: StoragePreviewObject | undefined;
  type: StoragePreviewType | undefined;
  /** Set when the object is not read at all because it is larger than its kind previews. */
  tooLarge?: { limit: number };
  /** Set when the object's kind has no viewer in the app (PDF): it is not read. */
  noViewer?: boolean;
  data: StoragePreviewContent | undefined;
  /** `data` belongs to the previously shown object while this one loads. */
  isStale: boolean;
  isLoading: boolean;
  error: StorageOperationError | undefined;
  reload: () => void;
};

/**
 * The object to show: a change between two objects waits `SETTLE_MS` (arrow-key travel reads only where it stops); the
 * first selection and a deselection apply at once.
 */
function useSettledObject(next: StoragePreviewObject | undefined): StoragePreviewObject | undefined {
  const [shown, setShown] = useState(next);
  const latest = useRef(next);
  latest.current = next;
  const nextKey = next?.versionKey;
  const shownKey = shown?.versionKey;
  // Keyed by version, not identity: callers may hand a fresh but equal object per render without restarting the wait.
  useEffect(() => {
    if (nextKey === shownKey) return undefined;
    if (shownKey === undefined || nextKey === undefined) {
      setShown(latest.current);
      return undefined;
    }
    const timer = setTimeout(() => setShown(latest.current), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [nextKey, shownKey]);
  return shown;
}

export const storagePreviewQueryKey = (connectionId: string, versionKey: string, maxBytes: number) => ['storage-preview-content', connectionId, versionKey, maxBytes] as const;

/** The tab's Refresh re-reads what the pane shows, whatever its version key says. */
export function invalidateStoragePreviewContent(queryClient: QueryClient, connectionId: string): void {
  void queryClient.invalidateQueries({ queryKey: ['storage-preview-content', connectionId] });
}

/**
 * A listing without a content type (Azure Files) previews by extension; when the read answers a type that previews
 * differently (an extensionless JSON or image), that viewer is used — read again whole when its plan allows more.
 */
async function readWithType(read: (maxBytes: number) => Promise<StorageContentReadResult>, object: StoragePreviewObject, type: StoragePreviewType, maxBytes: number) {
  const first = await read(maxBytes);
  if (type.kind !== 'unknown') return { result: first, type };
  const refined = previewTypeOf(object.path, first.contentType);
  if (refined.kind === 'unknown') return { result: first, type };
  const plan = previewReadPlan(refined.kind, first.size);
  if (!('maxBytes' in plan)) return { result: first, type };
  return { result: first.truncated && plan.maxBytes > maxBytes ? await read(plan.maxBytes) : first, type: refined };
}

/**
 * `loadAllBytes`: "Load whole file" for this object — the size the service last reported (the listing's size may be
 * older: a file that grew since is read whole, not to its listed length).
 */
export function useStoragePreviewContent(args: { connectionId: string; object: StoragePreviewObject | undefined; loadAllBytes?: number; enabled: boolean }): StoragePreviewContentState {
  const object = useSettledObject(args.object);
  const type = useMemo(() => (object ? previewTypeOf(object.path, object.contentType) : undefined), [object]);
  const plan = object && type && !object.archived ? previewReadPlan(type.kind, Math.max(object.size, args.loadAllBytes ?? 0), !!args.loadAllBytes) : undefined;
  const maxBytes = plan && 'maxBytes' in plan ? plan.maxBytes : 0;
  const query = useQuery({
    queryKey: storagePreviewQueryKey(args.connectionId, object?.versionKey ?? '', maxBytes),
    enabled: args.enabled && !!object && maxBytes > 0,
    // Keyed by ETag / Last-Modified the cache never goes stale; an object without either (an NFS listing) is read again.
    staleTime: object && (object.etag || object.lastModified) ? Infinity : 0,
    gcTime: object && object.size > LARGE_READ_BYTES ? 0 : CACHE_MS,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<StoragePreviewContent> => {
      if (!object) throw new Error('No object to preview');
      // Whichever read is in flight is the one a superseded selection cancels.
      let requestId = '';
      const onAbort = () => { if (requestId) void cancelStorageRequest(requestId); };
      signal.addEventListener('abort', onAbort);
      try {
        const read = (bytes: number) => {
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          requestId = newStorageRequestId();
          return readStorageContent({ connectionId: args.connectionId, resource: object.ref, maxBytes: bytes, requestId, timeoutMs: READ_TIMEOUT_MS });
        };
        const { result, type: shownType } = await readWithType(read, object, type ?? previewTypeOf(object.path, object.contentType), maxBytes);
        return { ...result, objectKey: object.key, versionKey: object.versionKey, object, type: shownType };
      } finally {
        signal.removeEventListener('abort', onAbort);
      }
    },
  });
  const data = query.data;
  const isStale = !!data && (data.versionKey !== object?.versionKey || query.isPlaceholderData);
  return {
    object,
    type,
    tooLarge: plan && 'tooLarge' in plan ? { limit: plan.limit } : undefined,
    noViewer: !!plan && 'noViewer' in plan,
    data: maxBytes > 0 ? data : undefined,
    isStale,
    isLoading: maxBytes > 0 && (query.isFetching || isStale),
    error: query.error && !query.isFetching ? toStorageOperationError(query.error) : undefined,
    reload: () => void query.refetch(),
  };
}
